import {
  DatagridConfigurable,
  ExportButton,
  FilterButton,
  List,
  SelectColumnsButton,
  SelectInput,
  TextInput,
  TopToolbar,
  email,
} from 'react-admin';
import { TherapistsCommandPanel } from './TherapistsCommandPanel';
import { TherapistsFiltersAutoBroadenController } from './TherapistsFiltersAutoBroaden';
import { genericCSVExporter } from '@/util/genericCSVExporter';

import {
  accountStatuses,
  accountStatusTitleMap,
} from './AccountInfo';

import {
  renderExportColumns,
  renderGridColumns,
  renderOmitList,
} from '@/components/ConfigurableColumns';
import { columns } from './TherapistColumns';
import { therapistsCustomFetcher } from './therapistsCustomFetcher';
import { DefaultPagination } from '@/components/DefaultPagination';
import { createDateFilters } from '@/util/DateFilters';
import { ModernListActions } from '@/components/DefaultListActions';

const emailValidation = [email()];

const patientsPerWeekChoices = [
  {
    id: '',
    name: 'n/a',
  },
  {
    id: '<=5',
    name: '5 or fewer',
  },
  {
    id: '6-10',
    name: '6 to 10',
  },
  {
    id: '11-20',
    name: '11 to 20',
  },
  {
    id: '21-30',
    name: '21 to 30',
  },
  {
    id: '>=31',
    name: '31 or more',
  },
];

export const practiceTypes = [
  { name: 'Solo Practice', id: 'solo' },
  { name: 'Group Practice', id: 'group' },
  { name: 'Under Supervision', id: 'supervised' },
  { name: 'Student', id: 'student' },
  { name: 'Other', id: 'other' },
];

const defaultFilters = {
  isHiddenTestAccount: 'hidden',
  deletedAt: 'hidden',
  signupCompletedAt: 'hidden',
  notificationsEnabled: 'all',
  createdAtDatePreset: 'last7days',
};

const appleIntegrationConnectedChoices = [
  { id: 'all', name: 'All' },
  { id: 'connected', name: 'Connected' },
  { id: 'pending', name: 'Pending' },
];

const therapistFilters = [
  <TextInput label="Id" key="id" source="_id" defaultValue="" />,
  ...createDateFilters('createdAt', 'Created', {
    datePresetPinned: true,
    collapseDateRangeUnlessCustom: true,
  }),
  <TextInput
    label="Phone Number"
    key="phoneNumber"
    source="phoneNumber"
    defaultValue=""
  />,
  <SelectInput
    label="Clients/week"
    key="onboardingResponses_patientsPerWeek"
    choices={patientsPerWeekChoices}
    source="onboardingResponses_patientsPerWeek"
  />,
  <SelectInput
    label="Practice Type"
    key="onboardingResponses_practiceType"
    choices={practiceTypes}
    source="onboardingResponses_practiceType"
  />,
  <SelectInput
    key="accountInfo.status"
    label="Account Status"
    source="accountInfo$status"
    choices={accountStatuses.map(status => ({
      id: status,
      name: accountStatusTitleMap[status],
    }))}
  />,
  <SelectInput
    key="appleIntegrationConnected"
    label="Apple Sign In"
    source="appleIntegrationConnected"
    choices={appleIntegrationConnectedChoices}
  />,
  <SelectInput
    key="notificationsEnabled"
    label="Notifications"
    source="notificationsEnabled"
    choices={[
      { id: 'all', name: 'All' },
      { id: 'enabled', name: 'Enabled' },
      { id: 'disabled', name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="nextSessionReminderAt"
    label="Onboarding Reminder"
    source="nextSessionReminderAt"
    choices={[
      { id: 'both', name: 'Both' },
      { id: 'set', name: 'Set' },
      { id: 'pending', name: 'Pending' },
    ]}
  />,
  <SelectInput
    key="accountInfo.isActive"
    label="Service Availability"
    source="accountInfo$isActive"
    choices={[
      { id: true, name: 'Available' },
      { id: false, name: 'Suspended' },
    ]}
  />,
  <TextInput
    label="Email"
    key="email"
    source="email"
    validate={emailValidation}
    defaultValue=""
  />,
  <TextInput
    key="firstName"
    defaultValue=""
    source="firstName"
    label="First Name"
  />,
  <TextInput
    key="lastName"
    defaultValue=""
    source="lastName"
    label="Last Name"
  />,
  <SelectInput
    key="isTestTherapist"
    label="Debug Menu"
    source="isTestTherapist"
    choices={[
      { id: true, name: 'Enabled' },
      { id: false, name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="calendarIntegration"
    label="Calendar Integration"
    source="calendarIntegration"
    choices={[
      { id: 'both', name: 'Both' },
      { id: 'connected', name: 'Connected' },
      { id: 'disconnected', name: 'Not Connected' },
    ]}
  />,
  <TextInput
    key="appleLoginUserId"
    defaultValue=""
    source="appleLoginUserId"
    label="Apple SSO User ID"
  />,
  <SelectInput
    key="advancedDebugMenuEnabled"
    label="Advanced Debug Menu"
    source="advancedDebugMenuEnabled"
    choices={[
      { id: true, name: 'Enabled' },
      { id: false, name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="notificationsEnabled"
    label="Advanced Debug Menu"
    source="notificationsEnabled"
    choices={[
      { id: true, name: 'Enabled' },
      { id: false, name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="verbosePipelineLoggingEnabled"
    label="Verbose Pipeline Telemetry"
    source="verbosePipelineLoggingEnabled"
    choices={[
      { id: true, name: 'Enabled' },
      { id: false, name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="otaUpdatesMandatory"
    label="OTA Updates Mandatory"
    source="otaUpdatesMandatory"
    choices={[
      { id: true, name: 'Enabled' },
      { id: false, name: 'Disabled' },
    ]}
  />,
  <SelectInput
    key="deletedAt"
    label="Deleted Items"
    source="deletedAt"
    alwaysOn
    emptyText="Hidden"
    emptyValue="hidden"
    choices={[
      { id: 'hidden', name: 'Hidden' },
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => {
      return value || 'hidden';

      const parsed = JSON.parse(value?.$$raw || 'null');

      if (!parsed || parsed.$eq === null) {
        return 'hidden';
      }

      if (parsed.$ne === true) {
        return 'visible';
      }

      return 'only';
    }}
  />,
  <SelectInput
    key="isHiddenTestAccount"
    label="Test Accounts"
    source="isHiddenTestAccount"
    emptyText="Hidden"
    emptyValue="hidden"
    alwaysOn
    choices={[
      // { id: "hidden", name: "Hidden" },
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => {
      return value || 'hidden';

      const parsed = JSON.parse(value?.$$raw || 'null');

      console.log('value - isHiddenTestAccount', value, parsed);

      if (!parsed || parsed.$ne === true) {
        return 'hidden';
      }

      if (parsed.$ne === 1) {
        return 'visible';
      }

      return 'only';
    }}
    // parse={(value) => {
    //   switch (value) {
    //     case "hidden":
    //       return defaultTestAccountFilter;
    //     case "visible":
    //       return { $$raw: JSON.stringify({ $ne: 1 }) };
    //     case "only":
    //       return { $$raw: JSON.stringify({ $eq: true }) };
    //   }
    // }}
  />,
  <SelectInput
    key="signupCompletedAt"
    label="Incomplete Signups"
    source="signupCompletedAt"
    alwaysOn
    emptyText="Hidden"
    emptyValue="hidden"
    choices={[
      // { id: "hidden", name: "Hidden" },
      { id: 'visible', name: 'Visible' },
      { id: 'only', name: 'Only' },
    ]}
    format={value => value || 'hidden'}
    // parse={(value) => {
    //   switch (value) {
    //     case "hidden":
    //       return defaultSignupCompletedAtFilter;
    //     case "visible":
    //       return { $$raw: JSON.stringify({ $ne: true }) };
    //     case "only":
    //       return { $$raw: JSON.stringify({ $eq: null }) };
    //   }
    // }}
  />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <SelectColumnsButton />
    <ExportButton
      maxResults={1000000}
      exporter={documentsCsvExporter}
    />
  </TopToolbar>
);

export const TherapistsList = () => (
  <List
    resource="therapists"
    perPage={25}
    filters={therapistFilters}
    filterDefaultValues={defaultFilters}
    sort={{ field: 'createdAt', order: 'DESC' }}
    actions={
      <ModernListActions
        collectionName="therapists"
        exporter={documentsCsvExporter}
      />
    }
    pagination={<DefaultPagination />}
    empty={false}
    queryOptions={{
      meta: {
        facet: false,
        raw: true,
        customFetcher: therapistsCustomFetcher,
      },
    }}
  >
    <TherapistsFiltersAutoBroadenController />
    <DatagridConfigurable
      bulkActionButtons={false}
      expand={TherapistsCommandPanel}
      omit={renderOmitList(columns)}
    >
      {renderGridColumns(columns)}
    </DatagridConfigurable>
  </List>
);

const documentsCsvExporter = genericCSVExporter(
  'therapists',
  renderExportColumns(columns),
);
